(() => {
  'use strict';

  const CAMPAIGN = 'guya_ncb_flow_v1';
  const LEGACY_CAMPAIGNS = new Set([
    'new_customer_buy_2_get_1',
    'new_customer_buy_2_get_1_v2'
  ]);

  if (customElements.get('guya-new-customer-builder')) return;

  class GuyaNewCustomerBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.mobileMedia?.addEventListener('change', this.boundResponsiveOrder);
        this.syncResponsiveOrder();
        return;
      }
      this.initialized = true;
      try {
        this.messages = JSON.parse(this.querySelector('[data-guya-ncb-i18n]')?.textContent || '{}');
      } catch (error) {
        this.messages = {};
      }

      this.requiredQuantity = Number.parseInt(this.dataset.requiredQuantity || '2', 10);
      this.requiredQuantity = Number.isFinite(this.requiredQuantity) ? this.requiredQuantity : 2;
      this.currency = this.dataset.currency || 'EUR';
      this.locale = this.dataset.locale || 'de-DE';
      this.knownExistingCustomer = this.dataset.knownExistingCustomer === 'true';
      this.cartRoot = this.dataset.cartRoot || '/';
      if (!this.cartRoot.endsWith('/')) this.cartRoot += '/';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.selectedGift = null;
      this.submitting = false;
      this.numberFormatter = this.createMoneyFormatter();
      this.layout = this.querySelector('.guya-ncb__layout');
      this.catalog = this.querySelector('.guya-ncb__catalog');
      this.sidebar = this.querySelector('.guya-ncb__sidebar');
      this.summary = this.querySelector('.guya-ncb__summary');
      this.summaryAnchor = document.createComment('guya-ncb-summary-anchor');
      this.summary?.before(this.summaryAnchor);
      this.mobileMedia = window.matchMedia('(max-width: 989px)');
      this.boundResponsiveOrder = this.syncResponsiveOrder.bind(this);

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.mobileMedia.addEventListener('change', this.boundResponsiveOrder);
      this.syncResponsiveOrder();
      this.render();
    }

    disconnectedCallback() {
      this.mobileMedia?.removeEventListener('change', this.boundResponsiveOrder);
    }

    syncResponsiveOrder() {
      if (!this.layout || !this.catalog || !this.sidebar || !this.summary) return;

      if (this.mobileMedia.matches) {
        this.layout.insertBefore(this.sidebar, this.catalog);
        this.catalog.after(this.summary);
        return;
      }

      this.layout.insertBefore(this.catalog, this.sidebar);
      this.summaryAnchor?.after(this.summary);
    }

    t(key, values = {}) {
      return String(this.messages?.[key] || key).replace(/\[\[(\w+)\]\]/g, (token, name) =>
        Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : token
      );
    }

    createMoneyFormatter() {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2
        });
      } catch (error) {
        return new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2
        });
      }
    }

    formatMoney(cents) {
      return this.numberFormatter.format((Number(cents) || 0) / 100);
    }

    productCards() {
      return Array.from(this.querySelectorAll('[data-product-card]'));
    }

    giftCards() {
      return Array.from(this.querySelectorAll('[data-gift-card]'));
    }

    readProductVariant(card) {
      const control = card.querySelector('[data-variant-select]');
      if (!control) return null;

      if (control.tagName === 'SELECT') {
        const option = control.selectedOptions[0];
        if (!option) return null;
        return {
          id: option.value,
          priceCents: Number.parseInt(option.dataset.priceCents || '0', 10),
          available: option.dataset.available === 'true'
        };
      }

      return {
        id: control.value,
        priceCents: Number.parseInt(control.dataset.priceCents || '0', 10),
        available: control.dataset.available === 'true'
      };
    }

    syncGiftVariant(card) {
      const control = card.querySelector('[data-gift-variant-select]');
      if (!control) return;

      const option = control.selectedOptions[0];
      if (!option) return;

      card.dataset.variantId = option.value;
      card.dataset.priceCents = option.dataset.priceCents || '0';
      card.dataset.available = option.dataset.available || 'false';
    }

    quantity(card) {
      return Math.max(0, Number.parseInt(card.dataset.quantity || '0', 10) || 0);
    }

    setQuantity(card, nextQuantity, moveFocusOnUnlock = false) {
      const beforeQuantity = this.totals().quantity;
      const previousGift = this.selectedGift;
      const variant = this.readProductVariant(card);
      const safeQuantity = Math.max(0, Math.min(20, Number(nextQuantity) || 0));
      card.dataset.quantity = variant && variant.available ? String(safeQuantity) : '0';
      this.clearError();
      this.render();

      const afterQuantity = this.totals().quantity;
      if (beforeQuantity < this.requiredQuantity && afterQuantity >= this.requiredQuantity) {
        this.announce(
          this.knownExistingCustomer
            ? this.t('message_30be1e3124')
            : this.t('message_1c230c3b84')
        );
        if (moveFocusOnUnlock && !this.knownExistingCustomer) {
          window.setTimeout(() => this.focusFirstGift(false), 0);
        }
      } else if (beforeQuantity >= this.requiredQuantity && afterQuantity < this.requiredQuantity) {
        this.announce(
          previousGift
            ? this.t('message_b46c2e91af', { p0: this.requiredQuantity })
            : this.t('message_abbdb9edcc', { p0: this.requiredQuantity })
        );
      } else {
        this.announce(this.t('message_c557ea6fb0', { p0: card.dataset.productTitle, p1: this.quantity(card) }));
      }
    }

    totals() {
      return this.productCards().reduce(
        (result, card) => {
          const quantity = this.quantity(card);
          const variant = this.readProductVariant(card);
          if (!variant || !variant.available || quantity < 1) return result;
          result.quantity += quantity;
          result.subtotalCents += quantity * variant.priceCents;
          return result;
        },
        { quantity: 0, subtotalCents: 0 }
      );
    }

    onClick(event) {
      const control = event.target.closest('[data-action]');
      if (!control || !this.contains(control)) return;

      const action = control.dataset.action;
      if (action === 'increment' || action === 'decrement') {
        const card = control.closest('[data-product-card]');
        if (!card) return;
        const delta = action === 'increment' ? 1 : -1;
        this.setQuantity(card, this.quantity(card) + delta, event.detail === 0);
        return;
      }

      if (action === 'choose-gift') {
        const card = control.closest('[data-gift-card]');
        const { quantity } = this.totals();
        if (this.knownExistingCustomer) {
          this.announce(this.t('message_6d61a3da29'));
          return;
        }
        if (!card || quantity < this.requiredQuantity || card.dataset.available !== 'true') {
          this.announce(this.t('message_e41a794bed', { p0: this.requiredQuantity }));
          return;
        }
        this.selectedGift = card;
        this.clearError();
        this.render();
        this.announce(
          this.t('message_4c9be6eac8', { p0: card.dataset.productTitle })
        );
        return;
      }

      if (action === 'clear-gift') {
        if (this.submitting) return;
        const previousChoice = this.selectedGift?.querySelector('[data-action="choose-gift"]');
        this.selectedGift = null;
        this.clearError();
        this.render();
        window.setTimeout(() => previousChoice?.focus(), 0);
        this.announce(this.t('message_24342c64af'));
        return;
      }

      if (action === 'checkout') {
        this.submit();
      }
    }

    onKeydown(event) {
      const current = event.target.closest('[data-action="choose-gift"]');
      if (!current) return;

      const supportedKeys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
      if (!supportedKeys.includes(event.key)) return;

      const choices = this.giftCards()
        .map((card) => card.querySelector('[data-action="choose-gift"]'))
        .filter((choice) => choice && !choice.disabled);
      if (choices.length < 1) return;

      event.preventDefault();
      const currentIndex = Math.max(0, choices.indexOf(current));
      let nextIndex = currentIndex;

      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = choices.length - 1;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % choices.length;
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + choices.length) % choices.length;
      }

      choices[nextIndex].focus();
      choices[nextIndex].click();
    }

    onChange(event) {
      if (this.submitting) return;

      if (event.target.matches('[data-variant-select]')) {
        const card = event.target.closest('[data-product-card]');
        if (!card) return;
        const variant = this.readProductVariant(card);
        if (!variant || !variant.available) card.dataset.quantity = '0';
        this.clearError();
        this.render();
        return;
      }

      if (event.target.matches('[data-gift-variant-select]')) {
        const card = event.target.closest('[data-gift-card]');
        if (!card) return;
        this.syncGiftVariant(card);
        if (this.selectedGift === card && card.dataset.available !== 'true') {
          this.selectedGift = null;
        }
        this.clearError();
        this.render();
      }
    }

    renderProduct(card) {
      const variant = this.readProductVariant(card);
      const available = Boolean(variant && variant.available);
      const quantity = available ? this.quantity(card) : 0;
      if (!available) card.dataset.quantity = '0';

      card.classList.toggle('is-selected', quantity > 0);
      card.classList.toggle('is-unavailable', !available);

      const output = card.querySelector('[data-quantity-output]');
      if (output) output.textContent = String(quantity);

      const price = card.querySelector('[data-card-price]');
      if (price && variant) price.textContent = this.formatMoney(variant.priceCents);

      const decrement = card.querySelector('[data-action="decrement"]');
      if (decrement) decrement.disabled = quantity < 1 || this.submitting;

      const variantSelect = card.querySelector('[data-variant-select]');
      if (variantSelect) variantSelect.disabled = this.submitting;

      card.querySelectorAll('[data-action="increment"]').forEach((button) => {
        button.disabled = !available || quantity >= 20 || this.submitting;
      });

      const addButton = card.querySelector('.guya-ncb__add-button');
      if (addButton) {
        addButton.textContent = available ? (quantity > 0 ? this.t('message_58fc98795b') : this.t('message_cbb95cf02b')) : this.t('message_a7d289ddd5');
      }
    }

    renderGift(card, unlocked) {
      this.syncGiftVariant(card);
      const available = card.dataset.available === 'true';
      const selected = !this.knownExistingCustomer && this.selectedGift === card && available;
      const locked = this.knownExistingCustomer || !unlocked;

      card.classList.toggle('is-locked', locked);
      card.classList.toggle('is-selected', selected);
      card.classList.toggle('is-unavailable', !available);

      const choice = card.querySelector('[data-action="choose-gift"]');
      if (choice) {
        choice.disabled = locked || !available || this.submitting;
        choice.setAttribute('aria-checked', String(selected));
      }

      const variantSelect = card.querySelector('[data-gift-variant-select]');
      if (variantSelect) variantSelect.disabled = this.submitting;

      const priceCents = Number.parseInt(card.dataset.priceCents || '0', 10);
      const price = card.querySelector('[data-gift-price]');
      if (price) price.textContent = this.t('message_bf13740d03', { p0: this.formatMoney(priceCents) });

      const state = card.querySelector('[data-gift-state]');
      if (!state) return;

      if (this.knownExistingCustomer) {
        state.textContent = this.t('message_9dbab446a1');
      } else if (!available) {
        state.textContent = this.t('message_c065a52117');
      } else if (locked) {
        state.textContent = this.t('message_b8d7ef601a', { p0: this.requiredQuantity });
      } else {
        state.textContent = selected
          ? this.t('message_c225c129a2')
          : this.t('message_8f3dd3161c');
      }
    }

    renderSteps(unlocked, ready, hasGift) {
      const productStep = this.querySelector('[data-step="products"]');
      const giftStep = this.querySelector('[data-step="gift"]');
      const checkoutStep = this.querySelector('[data-step="checkout"]');

      [productStep, giftStep, checkoutStep].forEach((step) => {
        if (!step) return;
        step.classList.remove('is-active', 'is-complete', 'is-optional');
        step.removeAttribute('aria-current');
        step.removeAttribute('aria-label');
      });

      if (productStep) productStep.classList.add(unlocked ? 'is-complete' : 'is-active');
      if (giftStep && hasGift) giftStep.classList.add('is-complete');
      if (giftStep && unlocked && !hasGift && !this.knownExistingCustomer) giftStep.classList.add('is-active');
      if (giftStep && this.knownExistingCustomer) giftStep.classList.add('is-optional');
      if (checkoutStep && ready) checkoutStep.classList.add('is-active');

      [productStep, giftStep, checkoutStep].forEach((step) => {
        if (!step) return;
        const label = step.querySelector('span:last-child')?.textContent?.trim() || '';
        if (step.classList.contains('is-active')) {
          step.setAttribute('aria-current', 'step');
          step.setAttribute('aria-label', this.t('message_18fafed0a9', { p0: label }));
        } else if (step.classList.contains('is-complete')) {
          step.setAttribute('aria-label', this.t('message_f199a48fdc', { p0: label }));
        } else if (step.classList.contains('is-optional')) {
          step.setAttribute('aria-label', this.t('message_23e89e7437', { p0: label }));
        }
      });
    }

    syncGiftTabStops() {
      const choices = this.giftCards()
        .map((card) => card.querySelector('[data-action="choose-gift"]'))
        .filter((choice) => choice && !choice.disabled);

      choices.forEach((choice) => {
        choice.tabIndex = -1;
      });

      const preferred = this.selectedGift?.querySelector('[data-action="choose-gift"]') || choices[0];
      if (preferred && !preferred.disabled) preferred.tabIndex = 0;
    }

    render() {
      this.productCards().forEach((card) => this.renderProduct(card));

      const { quantity, subtotalCents } = this.totals();
      const unlocked = quantity >= this.requiredQuantity;

      if (this.knownExistingCustomer || !unlocked || (this.selectedGift && !this.selectedGift.isConnected)) {
        this.selectedGift = null;
      }

      this.giftCards().forEach((card) => this.renderGift(card, unlocked));
      if (this.selectedGift && this.selectedGift.dataset.available !== 'true') this.selectedGift = null;
      this.syncGiftTabStops();

      const ready = unlocked && (this.knownExistingCustomer || Boolean(this.selectedGift));
      const remaining = Math.max(0, this.requiredQuantity - quantity);
      const cappedQuantity = Math.min(quantity, this.requiredQuantity);
      const progress = Math.min(quantity / this.requiredQuantity, 1) * 100;

      const catalogCounter = this.querySelector('[data-catalog-counter]');
      if (catalogCounter) catalogCounter.textContent = this.t('message_ae51a267ff', { p0: cappedQuantity, p1: this.requiredQuantity });

      const progressCard = this.querySelector('[data-progress-card]');
      if (progressCard) progressCard.classList.toggle('is-unlocked', unlocked);

      const progressTitle = this.querySelector('[data-progress-title]');
      if (progressTitle) {
        progressTitle.textContent = unlocked
          ? this.knownExistingCustomer
            ? this.t('message_4642c2a444')
            : this.t('message_35fbf35325')
          : this.t('message_787f89feca', { p0: remaining, p1: remaining === 1 ? this.t('message_34b1576075') : this.t('message_ff09a8b6df') });
      }

      const progressCopy = this.querySelector('[data-progress-copy]');
      if (progressCopy) progressCopy.textContent = this.t('message_981af75dea', { p0: cappedQuantity, p1: this.requiredQuantity });

      const progressTrack = this.querySelector('[data-progress-track]');
      if (progressTrack) progressTrack.setAttribute('aria-valuenow', String(cappedQuantity));

      const progressFill = this.querySelector('[data-progress-fill]');
      if (progressFill) progressFill.style.width = `${progress}%`;

      this.renderSteps(unlocked, ready, Boolean(this.selectedGift));

      const giftRadiogroup = this.querySelector('[data-gift-radiogroup]');
      if (giftRadiogroup) {
        giftRadiogroup.setAttribute('aria-required', String(!this.knownExistingCustomer));
        giftRadiogroup.setAttribute('aria-disabled', String(this.knownExistingCustomer));
      }

      const clearGift = this.querySelector('[data-action="clear-gift"]');
      if (clearGift) {
        clearGift.hidden = !this.selectedGift;
        clearGift.disabled = this.submitting;
      }

      const summaryCount = this.querySelector('[data-summary-count]');
      if (summaryCount) summaryCount.textContent = `${quantity} ${quantity === 1 ? this.t('message_34b1576075') : this.t('message_ff09a8b6df')}`;

      this.querySelectorAll('[data-summary-subtotal], [data-summary-total], [data-mobile-total]').forEach((node) => {
        node.textContent = this.formatMoney(subtotalCents);
      });

      const giftSummary = this.querySelector('[data-summary-gift]');
      if (giftSummary) {
        if (this.knownExistingCustomer) {
          giftSummary.textContent = this.t('message_45307e29a0');
        } else if (!this.selectedGift) {
          giftSummary.textContent = unlocked ? this.t('message_a1855f7533') : this.t('message_ed0faa4ec9');
        } else {
          const giftTitle = this.selectedGift.dataset.productTitle;
          giftSummary.textContent = this.t('message_2de346be0e', { p0: giftTitle });
        }
      }

      const mobileLabel = this.querySelector('[data-mobile-label]');
      if (mobileLabel) {
        mobileLabel.textContent = this.knownExistingCustomer && unlocked
          ? this.t('message_a1df7ae16c')
          : ready
            ? this.t('message_cc29a1010f')
          : unlocked
            ? this.t('message_6bc3af2eaa')
            : this.t('message_874cb4b81a', { p0: cappedQuantity, p1: this.requiredQuantity });
      }

      this.querySelectorAll('[data-checkout]').forEach((button) => {
        button.disabled = !unlocked || this.submitting;
        if (this.submitting) {
          button.textContent = this.t('message_6362b0786c');
        } else if (!unlocked) {
          button.textContent = this.t('message_cd44eee5d7', { p0: remaining, p1: remaining === 1 ? this.t('message_34b1576075') : this.t('message_ff09a8b6df') });
        } else if (!this.selectedGift) {
          button.textContent = this.knownExistingCustomer ? this.t('message_d8155f02cd') : this.t('message_8141c3feb0');
        } else {
          button.textContent = this.t('message_d8155f02cd');
        }
      });
    }

    createRequestId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();

      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }

    buildCartItems(requestId) {
      const lineMap = new Map();

      this.productCards().forEach((card) => {
        const quantity = this.quantity(card);
        const variant = this.readProductVariant(card);
        if (!variant || !variant.available || quantity < 1) return;

        const existing = lineMap.get(variant.id);
        if (existing) {
          existing.quantity += quantity;
          return;
        }

        lineMap.set(variant.id, {
          id: variant.id,
          quantity,
          properties: {
            _guya_ncb_role: 'qualifying_paid',
            _guya_ncb_campaign: CAMPAIGN,
            _guya_ncb_request_id: requestId
          }
        });
      });

      return Array.from(lineMap.values());
    }

    buildCartAttributes(requestId) {
      const emptyAttributes = {
        __guya_ncb_campaign: '',
        __guya_ncb_gift_variant_gid: '',
        __guya_ncb_gift_title: '',
        __guya_ncb_required_quantity: '',
        __guya_ncb_request_id: ''
      };

      if (this.knownExistingCustomer || !this.selectedGift) return emptyAttributes;

      const variantGid = String(this.selectedGift.dataset.variantGid || '');
      if (!/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(variantGid)) {
        throw new Error(this.t('message_0562a158b1'));
      }

      return {
        __guya_ncb_campaign: CAMPAIGN,
        __guya_ncb_gift_variant_gid: variantGid,
        __guya_ncb_gift_title: (this.selectedGift.dataset.productTitle || this.t('message_c58ed2d9b9')).slice(0, 120),
        __guya_ncb_required_quantity: String(this.requiredQuantity),
        __guya_ncb_request_id: requestId
      };
    }

    focusFirstGift(announceStatus = true) {
      const choice = this.giftCards()
        .map((card) => card.querySelector('[data-action="choose-gift"]'))
        .find((button) => button && !button.disabled);

      if (!choice) {
        this.showError(this.t('message_da32e53d19'));
        return;
      }

      choice.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'center' });
      window.setTimeout(() => choice.focus({ preventScroll: true }), this.reduceMotion ? 0 : 350);
      if (announceStatus) {
        this.announce(this.t('message_88e54f9b48'));
      }
    }

    async removePreviousCampaignLines() {
      const cartResponse = await fetch(`${this.cartRoot}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!cartResponse.ok) {
        throw new Error(this.t('message_658d113da9'));
      }

      const cart = await cartResponse.json();
      const updates = {};
      (cart.items || []).forEach((item) => {
        const legacyCampaign = item.properties?._guya_bundle_campaign;
        const currentCampaign = item.properties?._guya_ncb_campaign;
        if (LEGACY_CAMPAIGNS.has(legacyCampaign) || currentCampaign === CAMPAIGN) {
          updates[item.key] = 0;
        }
      });

      const updatePayload = {
        attributes: {
          __guya_ncb_campaign: '',
          __guya_ncb_gift_variant_gid: '',
          __guya_ncb_gift_title: '',
          __guya_ncb_required_quantity: '',
          __guya_ncb_request_id: ''
        }
      };
      if (Object.keys(updates).length > 0) updatePayload.updates = updates;

      const updateResponse = await fetch(`${this.cartRoot}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });

      if (!updateResponse.ok) {
        throw new Error(this.t('message_13ad5c1f41'));
      }
    }

    async updateCartAttributes(attributes) {
      const response = await fetch(`${this.cartRoot}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ attributes })
      });

      if (!response.ok) {
        throw new Error(this.t('message_1bb0820acf'));
      }
    }

    async verifyCampaignLines(requestId) {
      const response = await fetch(`${this.cartRoot}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) {
        throw new Error(this.t('message_09734c0780'));
      }

      const cart = await response.json();
      const campaignLines = (cart.items || []).filter(
        (item) => item.properties?._guya_ncb_campaign === CAMPAIGN
      );
      const hasCompetingRequest = campaignLines.some(
        (item) => item.properties?._guya_ncb_request_id !== requestId
      );
      const qualifyingQuantity = campaignLines.reduce((total, item) => {
        const properties = item.properties || {};
        if (
          properties._guya_ncb_request_id !== requestId ||
          properties._guya_ncb_role !== 'qualifying_paid'
        ) {
          return total;
        }
        return total + (Number.parseInt(item.quantity, 10) || 0);
      }, 0);

      if (hasCompetingRequest || qualifyingQuantity < this.requiredQuantity) {
        throw new Error(this.t('message_cae36b75ec'));
      }
    }

    async rollbackCampaignRequest(requestId) {
      const cartResponse = await fetch(`${this.cartRoot}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      const updates = {};
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        (cart.items || []).forEach((item) => {
          if (
            item.properties?._guya_ncb_campaign === CAMPAIGN &&
            item.properties?._guya_ncb_request_id === requestId
          ) {
            updates[item.key] = 0;
          }
        });
      }

      const payload = {
        attributes: {
          __guya_ncb_campaign: '',
          __guya_ncb_gift_variant_gid: '',
          __guya_ncb_gift_title: '',
          __guya_ncb_required_quantity: '',
          __guya_ncb_request_id: ''
        }
      };
      if (Object.keys(updates).length > 0) payload.updates = updates;

      await fetch(`${this.cartRoot}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
    }

    async submit() {
      if (this.submitting) return;

      const { quantity } = this.totals();
      if (quantity < this.requiredQuantity) {
        this.showError(this.t('message_a869160d21', { p0: this.requiredQuantity }));
        return;
      }

      if (!this.selectedGift && !this.knownExistingCustomer) {
        this.focusFirstGift();
        return;
      }

      if (window.Shopify && window.Shopify.designMode) {
        this.showError(this.t('message_7bf9a91b06'));
        return;
      }

      const requestId = this.createRequestId();
      const items = this.buildCartItems(requestId);
      if (items.length < 1) {
        this.showError(this.t('message_a1444af64f'));
        return;
      }

      let attributes;
      try {
        attributes = this.buildCartAttributes(requestId);
      } catch (error) {
        this.showError(error.message || this.t('message_8bb7932ee0'));
        return;
      }

      this.submitting = true;
      this.clearError();
      this.render();

      try {
        await this.removePreviousCampaignLines();
        const response = await fetch(`${this.cartRoot}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body.description || body.message || this.t('message_5916db4817'));
        }

        await this.updateCartAttributes(attributes);
        await this.verifyCampaignLines(requestId);
        window.location.assign(window.GuyaMoments ? window.GuyaMoments.navigationUrl(`${this.cartRoot}checkout`) : `${this.cartRoot}checkout`);
      } catch (error) {
        await this.rollbackCampaignRequest(requestId).catch(() => {});
        this.submitting = false;
        this.showError(error.message || this.t('message_7196cf75a4'));
        this.render();
      }
    }

    showError(message) {
      const error = this.querySelector('[data-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
      error.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
    }

    clearError() {
      const error = this.querySelector('[data-error]');
      if (!error) return;
      error.textContent = '';
      error.hidden = true;
    }

    announce(message) {
      const announcer = this.querySelector('[data-announcer]');
      if (!announcer) return;
      announcer.textContent = '';
      window.setTimeout(() => {
        announcer.textContent = message;
      }, 30);
    }
  }

  customElements.define('guya-new-customer-builder', GuyaNewCustomerBuilder);
})();

